package com.shadowings.gero.data.lessons

import androidx.compose.ui.unit.dp
import com.shadowings.gero.data.repository.Hiero
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.IExplanation
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.Option
import com.shadowings.gero.domain.model.PhoneticKeyboardContent
import com.shadowings.gero.domain.model.QuizText
import com.shadowings.gero.domain.model.SingleSelectionContent
import com.shadowings.gero.domain.model.UniliteralExplanation
import com.shadowings.gero.domain.model.WordSpellingContent
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.component.flattenGardinerCodes
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.exp_content_word_reading_and_meaning
import gero.composeapp.generated.resources.exp_title_word_name
import gero.composeapp.generated.resources.hiero_detail_title
import gero.composeapp.generated.resources.quiz_ripasso_cosa_significa_gero
import gero.composeapp.generated.resources.quiz_ripasso_quale_gero_significa
import gero.composeapp.generated.resources.quiz_ripasso_traslitterazione
import gero.composeapp.generated.resources.quiz_spell_word_prompt
import gero.composeapp.generated.resources.swipe_up
import org.jetbrains.compose.resources.StringResource


fun List<String>.toHieroExplanation(
    hiero: List<Hiero>,
    languageCode: String
): List<UniliteralExplanation> {
    return this.mapNotNull {
        hiero.firstOrNull { h -> h.gardinerCode == it }?.let { hiero ->
            val ideogramma = hiero.getIdeogramma(languageCode)
            val determinativo = hiero.getDeterminativo(languageCode)
            UniliteralExplanation(
                id = it,
                title = Res.string.hiero_detail_title,
                titleArgs = listOf(if (ideogramma != "-") ideogramma else determinativo)
            )
        } ?: run {
            null
        }
    }
}

fun buildVocabContent(
    lessonTitle: StringResource,
    lessonIntro: StringResource,
    wordIds: List<String>,
    words: List<Word>,
    languageCode: String
): List<IExplanation> {
    val filteredWords = wordIds.mapNotNull { id -> words.find { it.id == id } }

    val introSlides = listOf(
        ExplanationContent(
            title = lessonTitle,
            content = lessonIntro,
            face = Faces.Happy,
            image = Res.drawable.swipe_up,
            imageSize = 0.35f,
        )
    )

    val wordSlides = filteredWords.map { word ->
        ExplanationContent(
            title = Res.string.exp_title_word_name,
            titleArgs = listOf(word.getMeaning(languageCode)),
            content = Res.string.exp_content_word_reading_and_meaning,
            contentArgs = listOf(
                word.reading,
                word.inlineMeaning(languageCode),
                word.getNote(languageCode)
            ),
            face = Faces.Happy,
            composable = { MdCWord(word.gardiner, rtl = false, cart = false, size = 96.dp) },
            wordId = word.id
        )
    }

    return introSlides + wordSlides
}

// Wrong options for a multiple-choice question: compared by displayed text, not by word id,
// so two different words sharing a meaning (ACQUA/ACQUA_LISTA, or German "Priester" for
// SACERDOTE/SACERDOTI) never show the correct answer twice or the same wrong option twice.
internal fun distinctDistractors(
    optionWords: List<Word>,
    word: Word,
    correctText: String,
    count: Int,
    text: (Word) -> String
): List<String> {
    fun norm(s: String) = s.trim().lowercase()
    return optionWords
        .asSequence()
        .filter { it.id != word.id }
        .map(text)
        .filter { it.isNotBlank() && norm(it) != norm(correctText) }
        .distinctBy(::norm)
        .toList()
        .shuffled()
        .take(count)
}

fun buildVocabQuizContent(
    explanations: List<IExplanation>,
    words: List<Word>,
    languageCode: String,
    additionalOptions: Set<String> = emptySet()
): List<IQuizContent> {
    val wordIds = explanations
        .filterIsInstance<ExplanationContent>()
        .mapNotNull { it.wordId }
        .toSet()

    val lessonWords = words.filter { it.id in wordIds }
    val optionWords = words.filter { it.id in wordIds || it.id in additionalOptions }

    val properNameIds = setOf(
        "HA", "DEN", "INEPU", "ITEN", "ANY", "SOKAR", "PTAH", "TAURET",
        "ITETI", "AKER", "IMN", "NFRHTP", "AMENEMHAT", "TUTANKHAMUN", "MENTUHOTEP",
        "OSIRIDE", "HORUS", "HATHOR", "RAMSES", "SNEFRU"
    )

    val keyboardQuizzes = lessonWords.map { word ->
        PhoneticKeyboardContent(
            sentence = listOf(word.gardiner, word.alt1, word.alt2).mapNotNull { it }.random(),
            info = "${word.reading} -> ${word.inlineMeaning(languageCode)}",
            reading = word.reading.toList().map { it.toString() },
            isProperName = word.id in properNameIds
        )
    }

    val selectReadingQuizzes = lessonWords.map { word ->
        val options = mutableListOf(Option(word.gardiner, correct = true))
        options.addAll(
            distinctDistractors(optionWords, word, correctText = word.gardiner, count = 2) { it.gardiner }
                .map { Option(it, correct = false) }
        )

        SingleSelectionContent(
            text = Res.string.quiz_ripasso_quale_gero_significa,
            textArgs = listOf(word.inlineMeaning(languageCode)),
            detail = "",
            options = options.shuffled(),
            info = QuizText.Resource(Res.string.quiz_ripasso_traslitterazione, listOf(word.reading))
        )
    }

    // Computed once for the whole lesson rather than per word: parsing every option word's
    // Gardiner string here is the same regardless of which word we're currently building a
    // spelling quiz for, so recomputing it inside the loop below would be wasted O(N²) work.
    val allOptionCodes = optionWords.flatMap { flattenGardinerCodes(it.gardiner) }.distinct()

    val spellWordQuizzes = lessonWords.map { word ->
        val correctCodes = flattenGardinerCodes(word.gardiner)
        val distractorCodes = allOptionCodes
            .filter { it !in correctCodes }
            .shuffled()
            .take(3)

        WordSpellingContent(
            prompt = Res.string.quiz_spell_word_prompt,
            promptArgs = listOf(word.inlineMeaning(languageCode)),
            gardiner = word.gardiner,
            blocks = (correctCodes + distractorCodes).shuffled(),
            transliteration = word.reading
        )
    }

    val selectMeaningQuizzes = lessonWords.map { word ->
        val meaning = word.getMeaning(languageCode)
        val options = mutableListOf(Option(meaning, correct = true))
        options.addAll(
            distinctDistractors(optionWords, word, correctText = meaning, count = 3) { it.getMeaning(languageCode) }
                .map { Option(it, correct = false) }
        )

        SingleSelectionContent(
            text = Res.string.quiz_ripasso_cosa_significa_gero,
            detail = word.gardiner,
            options = options.shuffled(),
            info = QuizText.Resource(Res.string.quiz_ripasso_traslitterazione, listOf(word.reading))
        )
    }

    return (keyboardQuizzes + selectReadingQuizzes + selectMeaningQuizzes + spellWordQuizzes).shuffled()
}
